import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="toast"
//
// Mejora progresiva del toast de confirmación (TC-134-1.2.7.4). El mensaje ya está pintado por el
// servidor y es legible sin este controlador; lo que añade aquí es todo prescindible:
//
//   - despega el aviso a la capa `z(toast)`,
//   - destapa el botón de cierre (nace `hidden` porque sin JS no habría quien lo atendiera),
//   - vuelve a insertar el contenido de la región `role="status"` para que el lector de pantalla
//     lo anuncie, y
//   - programa un auto-descarte.
//
// Nada de esto aporta información: si el JS no llega, el aviso sigue estando y se va al navegar.
export default class extends Controller {
  static targets = ['cerrar', 'live']

  static values = {
    // Un aviso de una línea se lee de sobra en este rato. No es un límite de tiempo sobre una
    // tarea: si la persona necesita más, con pasar el ratón o llegar con el tabulador se cancela.
    autoDescarteMs: { type: Number, default: 10000 },
    // El re-anuncio se hace tras un respiro: si el nodo vuelve en el mismo fotograma, el lector de
    // pantalla no llega a ver un cambio en la región.
    reanuncioMs: { type: Number, default: 100 },
    // Lo declara el servidor, que es quien sabe si hay enlace sin tener que mirar el DOM.
    //
    // Antes se contestaba con `querySelector('a')`, y eso ataba la respuesta al instante en que se
    // preguntaba: `reanuncia()` vacía la región viva de forma síncrona y la repuebla 100 ms después,
    // así que durante esa ventana el enlace no está en el árbol y la búsqueda decía que no lo había.
    // El aviso con enlace acababa auto-descartándose, que es justo lo que no debe pasar. Traerlo del
    // servidor quita la pregunta del DOM y con ella la dependencia del orden de `connect()`.
    tieneEnlace: Boolean
  }

  connect () {
    this.retenerLigado = this.retiene.bind(this)
    this.element.classList.add('rnk-Toast-flotante')
    if (this.hasCerrarTarget) this.cerrarTarget.hidden = false

    this.reanuncia()
    this.programaAutoDescarte()
  }

  disconnect () {
    this.cancelaAutoDescarte()
    clearTimeout(this.reanuncioTimeout)
  }

  descartar () {
    this.cancelaAutoDescarte()
    this.element.remove()
  }

  // Cancela el auto-descarte en cuanto hay señal de que alguien está atendiendo al aviso: el ratón
  // encima o el foco dentro. No se reprograma — quien se ha parado a leerlo cierra cuando quiera.
  retiene () {
    this.cancelaAutoDescarte()
  }

  // Una región viva solo anuncia CAMBIOS: el contenido que ya venía en el HTML de la página no lo
  // es. Sacar los nodos y devolverlos convierte el mensaje del servidor en un cambio, y así se
  // anuncia sin robarle el foco a nadie.
  reanuncia () {
    if (!this.hasLiveTarget) return

    const region = this.liveTarget
    const contenido = Array.from(region.childNodes)

    region.replaceChildren()
    this.reanuncioTimeout = setTimeout(() => region.replaceChildren(...contenido), this.reanuncioMsValue)
  }

  // Un aviso con enlace no se auto-descarta: se llevaría por delante algo con lo que se puede
  // interactuar antes de que dé tiempo a usarlo.
  programaAutoDescarte () {
    if (this.tieneEnlaceValue) return

    this.element.addEventListener('mouseenter', this.retenerLigado)
    this.element.addEventListener('focusin', this.retenerLigado)
    this.autoDescarteTimeout = setTimeout(() => this.descartar(), this.autoDescarteMsValue)
  }

  cancelaAutoDescarte () {
    clearTimeout(this.autoDescarteTimeout)
    this.element.removeEventListener('mouseenter', this.retenerLigado)
    this.element.removeEventListener('focusin', this.retenerLigado)
  }
}
